import React from 'react';
import { Link } from 'react-router-dom';
import { useTenant } from '../../../context/TenantContext';
import type { MarketingLab, MarketingLabKey, MarketingMatrixCourse } from '../../../context/TenantContext';
import { parseEm } from '../inlineEm';
import { useMarketingCourses } from '../MarketingData';
import { ShotFrame, Switcher } from './Shot';
import { LabSequence } from './LabSequence';
import { ReviseTryLesson } from './TryLesson';
import { CtaLink } from './links';
import { LABS } from './content';
import { REVISE_ANCHORS } from './sectionMeta';

const SECTION_FOR: Record<MarketingLabKey, 'labStudy' | 'labEssay' | 'labPractice'> = {
  study: 'labStudy',
  essay: 'labEssay',
  practice: 'labPractice',
};

const TopicRow: React.FC<{ row: MarketingMatrixCourse; href: string | null }> = ({ row, href }) => {
  const inner = (
    <>
      <span className="rv-tp">{row.track}</span>
      <span className="rv-tn">{row.title}</span>
      {row.price && <span className="rv-tf">{row.price}</span>}
    </>
  );
  return href ? (
    <Link className="rv-trow" to={href}>
      {inner}
    </Link>
  ) : (
    <div className="rv-trow">{inner}</div>
  );
};

/** Ruled note under a section — the Practice Labs giveaway statement.
 *
 *  Either half is optional. A label with no text is a standing statement
 *  rather than a preamble to one, and an empty `.rv-gifts-foot` would still
 *  draw its rule and its margins — a stray ruled gap under the heading. */
const Note: React.FC<{ label?: string; text?: string }> = ({ label, text }) => (
  <>
    {label && <p className="rv-kicker rv-freelabel">{label}</p>}
    {text && <p className="rv-gifts-foot">{parseEm(text)}</p>}
  </>
);

/**
 * One product section (Study / Essay / Practice Labs). All three render through
 * this; the `lab` prop selects which entry of `marketing.labs` (falling back to
 * the shipped defaults) it reads, and which accent the section carries.
 *
 * Render order is fixed and matches the argument each section makes:
 * head → demo → solo capture → before-notes → beats → sequence → switcher →
 * after-notes → topics. Not every section uses every slot, and each is skipped
 * when empty.
 *
 * The SEQUENCE slot (Essay and Practice, Aug 2026) is the one that is not
 * content-driven — it is bespoke JSX per lab, and `LabSequence.tsx` explains
 * why. It sits where the switcher used to because it replaced it: both of
 * those labs now ship with no `switcher` and no `solo`, so the two slots above
 * simply render nothing for them and stay available for anyone else.
 */
export const LabSection: React.FC<{ lab: MarketingLabKey }> = ({ lab }) => {
  const { tenant } = useTenant();
  const { courses } = useMarketingCourses();
  const override: MarketingLab | undefined = tenant?.branding.marketing?.labs?.[lab];
  const fallback = LABS[lab];

  const eyebrow = override?.eyebrow || fallback.eyebrow;
  const heading = override?.heading || fallback.heading || '';
  const lede = override?.lede || fallback.lede;
  const demo = override?.demo || fallback.demo;
  const solo = override?.solo || fallback.solo;
  const beats = override?.beats?.length ? override.beats : (fallback.beats ?? []);
  const switcher = override?.switcher?.length ? override.switcher : (fallback.switcher ?? []);
  const switcherLabel = override?.switcherLabel || fallback.switcherLabel;
  const notes = override?.notes?.length ? override.notes : (fallback.notes ?? []);
  const topics = override?.topics?.length ? override.topics : (fallback.topics ?? []);
  const topicsSummary = override?.topicsSummary || fallback.topicsSummary;
  /* Merged field by field, like everything above it. Taking the override
     object wholesale would mean a tenant repointing just `cta.href` — the one
     field content.ts tells them they can repoint — loses the label with it,
     and the button then fails the render guard below and silently disappears. */
  const cta = {
    label: override?.cta?.label || fallback.cta?.label,
    href: override?.cta?.href || fallback.cta?.href,
    note: override?.cta?.note || fallback.cta?.note,
  };

  /* Resolve a topic row to a real course so the catalogue table is navigable.
     Matches on the row's `id` first, then case-insensitively on title. A row
     with no match renders as plain text rather than a dead link — the table is
     a statement of what exists, and a topic that isn't published yet should
     still be listed. */
  const hrefFor = (row: MarketingMatrixCourse): string | null => {
    const byId = courses.find((c) => c.id === row.id);
    if (byId) return `/course/${byId.id}`;
    const needle = row.title.trim().toLowerCase();
    const byTitle = courses.find((c) => c.title.trim().toLowerCase() === needle);
    return byTitle ? `/course/${byTitle.id}` : null;
  };

  if (!heading && beats.length === 0 && !demo && switcher.length === 0) return null;

  const before = notes.filter((n) => n.position === 'before');
  const after = notes.filter((n) => n.position !== 'before');

  const topicRows = (
    <div className="rv-topics">
      {topics.map((row) => (
        <TopicRow key={row.id} row={row} href={hrefFor(row)} />
      ))}
    </div>
  );

  return (
    <section className="rv-sec" id={REVISE_ANCHORS[SECTION_FOR[lab]]} data-accent={lab}>
      <div className="rv-wrap">
        <div className="rv-head">
          {eyebrow && <p className="rv-kicker">{eyebrow}</p>}
          {heading && <h2>{parseEm(heading)}</h2>}
          {lede && <p className="rv-lede">{lede}</p>}
        </div>

        {demo && (
          /* No wrapper card. The lesson used to sit inside a panel inside this
             section — three bordered boxes deep, each with its own margin, and
             the pitch restated at every level. The lesson announces itself; it
             does not need a frame introducing it. Anything the block wants to
             say now sits in the section's own flow, and `#try` rides on the
             card itself so the nav lands on the thing rather than its preamble. */
          <div className="rv-demo-lead rv-band-centre">
            {demo.title && <h3>{demo.title}</h3>}
            {demo.body && <p className="rv-p">{demo.body}</p>}
            <ReviseTryLesson id="try" tag={demo.tag} />
          </div>
        )}

        {solo && (
          <div className="rv-figure rv-solo">
            <ShotFrame shot={solo.shot} />
            {solo.caption && <p className="rv-cap">{solo.caption}</p>}
          </div>
        )}

        {before.map((n, i) => (
          <Note key={n.label ?? n.text?.slice(0, 40) ?? i} label={n.label} text={n.text} />
        ))}

        {beats.length > 0 && (
          <div className="rv-beats">
            {beats.map((beat, i) => (
              <div key={beat.title ?? i} className={`rv-beat${beat.wide ? ' rv-beat-wide' : ''}`}>
                <div className="rv-beat-copy">
                  {beat.title && <h3>{beat.title}</h3>}
                  {beat.body?.map((para, p) => (
                    <React.Fragment key={para.slice(0, 40)}>
                      <p className="rv-p">
                        {parseEm(para, (children, key) => (
                          <strong key={key}>{children}</strong>
                        ))}
                      </p>
                      {beat.quote && p === 0 && <p className="rv-quote">&ldquo;{beat.quote}&rdquo;</p>}
                    </React.Fragment>
                  ))}
                </div>

                {beat.switcher?.length ? (
                  <Switcher panels={beat.switcher} label={beat.switcherLabel} />
                ) : beat.shot ? (
                  <div className="rv-figure">
                    <ShotFrame shot={beat.shot} />
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        )}

        <LabSequence lab={lab} />

        {switcher.length > 0 && (
          <div className="rv-section-switch">
            <Switcher panels={switcher} label={switcherLabel} />
          </div>
        )}

        {after.map((n, i) => (
          <Note key={n.label ?? n.text?.slice(0, 40) ?? i} label={n.label} text={n.text} />
        ))}

        {topics.length > 0 &&
          /* Twelve rows is a wall where six was a list, so the long version
             folds. `open` is deliberately absent: someone scrolling past the
             demo should meet the next argument, not a table of contents. */
          (topicsSummary ? (
            <details className="rv-fold">
              <summary>{topicsSummary}</summary>
              {topicRows}
            </details>
          ) : (
            topicRows
          ))}

        {cta.label && cta.href && (
          /* Hugs its content rather than stretching the way the hero's does —
             it closes an argument instead of opening one. */
          <div className="rv-ctas rv-sec-cta">
            <CtaLink className="rv-btn rv-btn-primary" href={cta.href}>
              {cta.label}
            </CtaLink>
            {cta.note && <p className="rv-cta-note">{cta.note}</p>}
          </div>
        )}
      </div>
    </section>
  );
};

export const ReviseLabStudy: React.FC = () => <LabSection lab="study" />;
export const ReviseLabEssay: React.FC = () => <LabSection lab="essay" />;
export const ReviseLabPractice: React.FC = () => <LabSection lab="practice" />;
